/** Badge & credential admin types — /api/v2/admin/badges/* */

export type BadgeApplicationStatus =
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'pending'
  | 'withdrawn'
  | string;

export type BadgeScope = 'user' | 'org' | 'both' | string;
export type BadgeAwardMethod = 'manual' | 'auto' | 'application' | string;

export interface BadgeType {
  btid: number;
  name: string;
  name_system?: string | null;
  description?: string | null;
  category?: string | null;
  tier?: string | number | null;
  scope?: BadgeScope;
  award_method?: BadgeAwardMethod;
  requires_verification?: boolean | number;
  active?: boolean | number;
  icon_url?: string | null;
  date_added?: string | null;
  date_updated?: string | null;
  [key: string]: unknown;
}

export interface BadgeRule {
  brid: number;
  btid: number;
  metric?: string | null;
  operator?: string | null;
  threshold?: number | string | null;
  label?: string | null;
  active?: boolean | number;
  date_added?: string | null;
  [key: string]: unknown;
}

export interface BadgeApplication {
  baid: number;
  btid: number;
  badge_name?: string | null;
  uid?: number | null;
  oid?: number | null;
  vid?: number | null;
  status: BadgeApplicationStatus;
  notes?: string | null;
  asid_json?: number[] | string | null;
  reviewer_notes?: string | null;
  date_submitted?: string | null;
  date_reviewed?: string | null;
  date_added?: string | null;
  [key: string]: unknown;
}

export interface BadgeApplicationsResponse {
  applications: BadgeApplication[];
  total: number;
  page: number;
  page_size: number;
}

export interface BadgeTypesResponse {
  types?: BadgeType[];
  badge_types?: BadgeType[];
  total?: number;
}

export interface BadgeRulesResponse {
  rules?: BadgeRule[];
  total?: number;
}

export interface BadgeStats {
  under_review?: number;
  approved?: number;
  rejected?: number;
  total_applications?: number;
  total_types?: number;
  awarded?: number;
  [key: string]: number | undefined;
}

export interface BadgeVerificationChecklistItem {
  vcid?: number;
  item_key?: string;
  status?: string;
  asid_json?: number[] | string | null;
  reviewer_notes?: string | null;
  [key: string]: unknown;
}

export interface BadgeVerification {
  vid: number;
  status?: string;
  checklist?: BadgeVerificationChecklistItem[];
  items?: BadgeVerificationChecklistItem[];
  [key: string]: unknown;
}

export interface BadgeApplicationsParams {
  status?: string;
  btid?: number | string;
  page?: number;
  page_size?: number;
}

export interface CreateBadgeTypePayload {
  name: string;
  name_system?: string;
  description?: string;
  category?: string;
  tier?: string;
  scope?: string;
  award_method?: string;
  requires_verification?: number | boolean;
  active?: number | boolean;
}

export type UpdateBadgeTypePayload = Partial<CreateBadgeTypePayload> & {
  btid: number;
};

export interface CreateBadgeRulePayload {
  btid: number;
  metric: string;
  threshold: number | string;
  operator: string;
  label?: string;
}

export type UpdateBadgeRulePayload = Partial<Omit<CreateBadgeRulePayload, 'btid'>> & {
  brid: number;
};

export interface AwardBadgePayload {
  btid: number | string;
  uid?: number | string;
  oid?: number | string;
  notes?: string;
  skip_verification?: number | boolean;
}

export interface RevokeBadgePayload {
  ubid?: number | string;
  obid?: number | string;
  notes?: string;
}

export interface DecideApplicationPayload {
  baid: number;
  reviewer_notes?: string;
}

/** Parse asid_json which may arrive as an array or JSON string. */
export function parseAsidJson(raw: BadgeApplication['asid_json']): number[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0);
      }
    } catch {
      // comma-separated fallback
      return trimmed
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
    }
  }
  return [];
}

export const APPLICATION_STATUS_BADGE: Record<string, string> = {
  under_review: 'bg-amber-100 text-amber-800',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  withdrawn: 'bg-gray-100 text-gray-600',
};
